import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Card, CardContent, CardActions, CardHeader, Divider, ThemeProvider, CssBaseline, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, AppBar, Toolbar } from '@mui/material';
import { useAuth } from '../context/Login.context';
import { useNavigate } from 'react-router-dom';
import PlanList from '../components/PlanList';
import { theme } from '../theme/theme';
import { cancelSubscription, getSubscribeCurrentPlan } from '../services/subcriptions';
import { Subscription } from '../interfaces/auth.insterface';

const Profile: React.FC = () => {
  const { user, logout, checkAuth } = useAuth();
  const navigate = useNavigate();

  const [currentSubscription, setCurrentSubscription] = useState<Subscription | null>(null);
  const [openCancelDialog, setOpenCancelDialog] = useState(false);

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const subscriptionData = await getSubscribeCurrentPlan();
        setCurrentSubscription(subscriptionData);
      } catch (error) {
        console.error('Error al obtener la suscripción actual:', error);
        setCurrentSubscription(null);
      }
    };
    fetchSubscription();
  }, []);

  const capitalizeFirstLetter = (string: string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/'); // Redirigir a la página de inicio después de cerrar sesión
  };

  const handleOpenCancelDialog = () => {
    setOpenCancelDialog(true);
  };

  const handleCloseCancelDialog = () => {
    setOpenCancelDialog(false);
  };

  const handleCancelSubscription = async () => {
    if (currentSubscription?.id) {
      try {
        await cancelSubscription(currentSubscription.id.toString());
        alert('Suscripción cancelada exitosamente.');
        setOpenCancelDialog(false);
        // Actualizar el estado del usuario o recargar para reflejar el cambio
        checkAuth(); // Esto debería refrescar la información del usuario, incluyendo la suscripción
      } catch (error) {
        console.error('Error al cancelar la suscripción:', error);
        alert('Error al cancelar la suscripción. Por favor, inténtalo de nuevo.');
      }
    }
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ background: theme.palette.background.default, minHeight: '100vh' }}>
        <AppBar position="static" sx={{ backgroundColor: 'rgba(255, 255, 255, 0.9)', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)' }}>
          <Toolbar sx={{ justifyContent: 'space-between', paddingY: { xs: 1, md: 2 } }}>
            <Typography variant="h6" component="div" sx={{ color: theme.palette.text.primary, fontWeight: 'bold', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
              Bienvenido, {user?.first_name || user?.email || 'Usuario'}!
            </Typography>
            <Box>
              {/* Botón "Ir a la app" */}
              <Button
                variant="contained"
                sx={{
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.common.white,
                  marginRight: 2,
                  '&:hover': {
                    backgroundColor: theme.palette.primary.dark,
                  },
                  fontSize: { xs: '0.8rem', md: '1rem' },
                  padding: { xs: '0.5rem 1rem', md: '0.75rem 1.5rem' }
                }}
                onClick={async () => {
                  await logout();
                  window.location.href = 'https://app.pueril-algoritmo.cl/';
                }}
              >
                Ir a la app
              </Button>
              <Button variant="outlined" color={"warning"} onClick={handleLogout} sx={{ mt: 0 }}>
                Cerrar Sesión
              </Button>
            </Box>
          </Toolbar>
        </AppBar>
        <Box sx={{ p: 3, textAlign: 'center', pt: 10 }}> {/* Added padding-top to account for fixed AppBar */}
          <Typography variant="body1" mb={3} sx={{ color: theme.palette.common.white }}>
            Este es tu perfil. Aquí puedes ver tu información.
          </Typography>

          {/* Sección Superior: Detalles del Usuario y Plan Actual */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, mt: 4 }}>
            <Card sx={{ flex: '1 1 300px', maxWidth: 400 }}>
              <CardHeader title="Detalles del Usuario" titleTypographyProps={{ color: theme.palette.primary.main }} />
              <CardContent>
                {user?.first_name && (
                  <Typography variant="body1">
                    <strong>Nombre:</strong> {capitalizeFirstLetter(user.first_name)}
                  </Typography>
                )}
                {user?.last_name && (
                  <Typography variant="body1">
                    <strong>Apellido:</strong> {capitalizeFirstLetter(user.last_name)}
                  </Typography>
                )}
                {user?.email && (
                  <Typography variant="body1">
                    <strong>Correo:</strong> {user.email}
                  </Typography>
                )}
              </CardContent>
            </Card>

            <Card sx={{ flex: '1 1 300px', maxWidth: 400 }}>
              <CardHeader title="Plan Actual" titleTypographyProps={{ color: theme.palette.primary.main }} />
              <CardContent>
                {currentSubscription?.plan?.name ? (
                  <>
                    <Typography variant="body1">
                      <strong>Plan:</strong> {currentSubscription.plan.name}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Fecha de Suscripción:</strong> {new Date(currentSubscription.start_date).toLocaleDateString()}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Fecha de Vencimiento:</strong> {new Date(currentSubscription.end_date).toLocaleDateString()}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Auto Renovación:</strong> {currentSubscription.auto_renew ? 'Sí' : 'No'}
                    </Typography>
                    <Typography variant="body1">
                      <strong>Tiempo Restante:</strong> {(():string => {
                        const endDate = new Date(currentSubscription.end_date);
                        const now = new Date();
                        const diffTime = Math.abs(endDate.getTime() - now.getTime());
                        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                        return `${diffDays} días`;
                      })()}
                    </Typography>
                    <CardActions sx={{ justifyContent: 'center', mt: 2 }}>
                      <Button size="small" variant="outlined" color="error" onClick={handleOpenCancelDialog} disabled={!currentSubscription.auto_renew}>
                        Suspender Plan
                      </Button>
                      <Button size="small" variant="contained" color="primary" onClick={() => alert('Cambiar de plan')}>
                        Cambiar de Plan
                      </Button>
                    </CardActions>
                  </>
                ) : (
                  <Typography variant="body1">
                    No tienes un plan activo. <Button size="small">Elige un plan</Button>
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Box>

          {/* Separador */}
          <Divider sx={{ my: 4 }} />

          {/* Sección Inferior: Seleccionar un Plan */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" component="h2" gutterBottom sx={{ color: theme.palette.common.white }}>
              Selecciona un Plan
            </Typography>
            <PlanList />
          </Box>

          {/* Diálogo de Confirmación de Cancelación */}
          <Dialog
            open={openCancelDialog}
            onClose={handleCloseCancelDialog}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
          >
            <DialogTitle id="alert-dialog-title">{"Confirmar Cancelación"}</DialogTitle>
            <DialogContent>
              <DialogContentText id="alert-dialog-description">
                ¿Estás seguro de que deseas cancelar tu suscripción actual? Esta acción no se puede deshacer.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseCancelDialog}>No, mantener</Button>
              <Button onClick={handleCancelSubscription} autoFocus color="error">
                Sí, cancelar
              </Button>
            </DialogActions>
          </Dialog>
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default Profile;